import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchQboConnectionStatus } from "../../store/thunks/qboThunk";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import QuickBooksConnectButton from "./QuickBooksConnectButton";
import QuickBooksSuccess from "./QuickBookSuccess";
import QuickBooksCashFlow from "./QuickBookCashFlow";
import QuickBooksSalesReport from "./QuickBookSalesReport";
import QuickBooksInvoices from "./QuickBooksInvoices";
import UploadSchedule from "./UploadSchedule";
import UploadClients from "./UploadClients";
import UploadProducts from "./UploadProducts";
import UploadAppointments from "./UploadAppointments";
import UploadServices from "./UploadServices";

// Define modules as a function that receives selectedClinic
const getModules = (selectedClinic) => [
  { name: "Cash Flow Summary", component: <QuickBooksCashFlow selectedClinic={selectedClinic} /> },
  { name: "Sales Report", component: <QuickBooksSalesReport selectedClinic={selectedClinic} /> },
  { name: "Invoices", component: <QuickBooksInvoices selectedClinic={selectedClinic} /> },
  { name: "Schedule", component: <UploadSchedule selectedClinic={selectedClinic} /> },
  { name: "Clients", component: <UploadClients selectedClinic={selectedClinic} /> },
  { name: "Products", component: <UploadProducts selectedClinic={selectedClinic} /> },
  { name: "Services", component: <UploadServices selectedClinic={selectedClinic} /> },
  { name: "Appointments", component: <UploadAppointments selectedClinic={selectedClinic} /> },
];

function QuickBooks() {
  const dispatch = useDispatch();
  const isConnected = useSelector((state) => state.qbo.isConnected); // Connection status from Redux
  const status = useSelector((state) => state.qbo.status); // Loading/success/failure status
  const clinics = useSelector((state) => state.clinics.clinics); // All clinics from Redux
  const clinicsStatus = useSelector((state) => state.clinics.status); // Clinics loading status
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState(""); // Local clinic selection

  // Handle clinic selection change
  const handleClinicChange = (event) => {
    const clinicId = event.target.value;
    setSelectedClinicId(clinicId);
  };

  // Get selected clinic object from clinicId
  const selectedClinic = clinics.find(c => c._id === selectedClinicId);
  
  // Get modules with selectedClinic
  const modules = selectedClinic ? getModules(selectedClinic) : [];

  // Fetch clinics on mount if not already loaded
  useEffect(() => {
    if (clinicsStatus === "idle") {
      dispatch(fetchClinics());
    }
  }, [dispatch, clinicsStatus]);

  // Fetch QuickBooks connection status when selectedClinicId changes
  useEffect(() => {
    if (selectedClinicId && selectedClinicId !== "") {
      dispatch(fetchQboConnectionStatus(selectedClinicId));
    }
  }, [dispatch, selectedClinicId]);

  return (
    <div>
      <div className="container mx-auto mt-5 p-4 bg-white rounded-sm shadow-lg text-center">
        <h1 className="text-2xl font-semibold mb-4">QuickBooks Integration</h1>
        
        {/* Clinic Selection */}
        {clinicsStatus === "loading" ? (
          <p className="text-gray-500">Loading clinics...</p>
        ) : clinics.length > 0 ? (
          <div className="mb-4">
            <label htmlFor="clinic-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Clinic:
            </label>
            <select
              id="clinic-select"
              value={selectedClinicId}
              onChange={handleClinicChange}
              className="block w-full max-w-xs mx-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select a clinic...</option>
              {clinics.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-gray-500">No clinics available.</p>
        )}

        {/* QuickBooks Connection Status */}
        {!selectedClinic ? (
          <p className="text-gray-500">Please select a clinic to manage QuickBooks integration.</p>
        ) : status === "loading" ? (
          <p className="text-gray-500">Checking QuickBooks connection...</p>
        ) : (
          <>
            <QuickBooksConnectButton selectedClinic={selectedClinic} />
            <QuickBooksSuccess selectedClinic={selectedClinic} />
          </>
        )}
      </div>

      {selectedClinic && (
        <>
          <div className="flex justify-center gap-2 mt-4">
            {modules.map((module) => (
              <button
                key={module.name}
                onClick={() => setSelectedModule(module.name)}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-opacity-50"
              >
                {module.name}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {modules.map(
              (module) =>
                selectedModule === module.name && (
                  <div key={module.name}>{module.component}</div>
                )
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default QuickBooks;
